import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Package, Warehouse, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import SearchBar from "../../../components/searchBar";
import DateRangeFilter, { rangeFromPreset } from "../../../components/dateRangeFilter/DateRangeFilter";
import CustomTable from "../../../components/customTable";
import useAuth from "../../../hooks/useAuth";
import { useUrlState, useUrlStateBatch } from "../../../hooks/useUrlState";
import { getAllPackages, getAllPackagesByStatus } from "../../../api/packages";
import {
  packageStatus,
  packageStatusStyles,
} from "../../../utils/packageStatus";
import { debounce } from "../../../utils/debounce";
import { showToast } from "../../../utils/showToast";
import { apiErrorMessage, isNetworkError } from "../../../utils/apiErrorMessage";
import { Skeleton } from "../../../components/ui/Skeleton";
import { Button } from "../../../components/ui/Button";
import { EmptyState } from "../../../components/ui/EmptyState";
import { TableSkeleton } from "../../../components/ui/TableSkeleton";
import { SummaryCardsSkeleton } from "../../../components/ui/SummaryCardsSkeleton";
import EvidenceView from "../../../components/evidenceView";
import moment from "moment";

export default function DeliveredView() {
  const { t } = useTranslation();
  const { user, isLoading } = useAuth();
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);
  const [inStorage, setInStorage] = useState(0);
  const [picked, setPicked] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Guarda el errorInfo del listado (no un booleano) para poder mostrar el texto
  // del backend; null = sin error.
  // Reintento manual tras un fallo del listado: solo re-dispara el fetch.
  const [reloadKey, setReloadKey] = useState(0);
  // URL-state: búsqueda + rango compartibles + back/forward, sembrados de forma
  // SÍNCRONA ANTES del primer fetch. Sin params ⇒ cada default == el de hoy ⇒
  // comportamiento idéntico. Las KPI-cards NO se convierten en filtro (siguen
  // siendo solo display: tocarían status:4 del fetch, que está prohibido).
  const [qParam, setQParam] = useUrlState("q", "");
  // Lectura de preset/from/to; la ESCRITURA de los tres se hace JUNTA con
  // useUrlStateBatch (una sola navegación). Con setters separados de useUrlState
  // las navegaciones se pisaban: react-router no encadena dos setSearchParams
  // síncronos, así que cada setter veía la URL vieja y solo quedaba el último
  // param → el preset/rango no se aplicaba bien. (Mismo bug que en adminPickupView.)
  const [datePreset] = useUrlState("preset", "7d");
  // Rango 'custom': se persiste como ?from/?to (YMD) además de ?preset=custom,
  // para que al recargar/compartir el rango se reconstruya en vez de colapsar a
  // HOY. Default "" ⇒ omitidos de la URL cuando el preset no es custom.
  const [fromParam] = useUrlState("from", "");
  const [toParam] = useUrlState("to", "");
  const setRange = useUrlStateBatch({
    preset: { type: "string", defaultValue: "7d" },
    from: { type: "string", defaultValue: "" },
    to: { type: "string", defaultValue: "" },
  });

  const [query, setQuery] = useState(() => qParam);
  // Seed capturado UNA sola vez para prefill del SearchBar desde ?q=. NO se pasa
  // el `query` reactivo (salida de un debounce) como value, porque revertía el
  // input mientras se tipeaba; este seed estable solo siembra el valor al montar.
  const q0 = useRef(query).current;
  const [viewEvidence, setViewEvidence] = useState(false);
  const [packageEvidence, setPackageEvidence] = useState({});
  const [filters, setFilters] = useState({});
  const [showResume, setShowResume] = useState(() => {
    const [s, e] =
      datePreset === "custom" && fromParam && toParam
        ? [moment(fromParam, "YYYY-MM-DD"), moment(toParam, "YYYY-MM-DD")]
        : rangeFromPreset(datePreset);
    return { startDate: s.format("MM/DD/YYYY"), endDate: e.format("MM/DD/YYYY") };
  });
  // visualViewport no existe en WebViews viejos; mismo fallback que allOrders.
  const height = window.visualViewport?.height || window.innerHeight || 800;

  const totalDocsPerPage = Math.floor((height * 0.6) / 55 - 2);

  const docsPerPage = totalDocsPerPage;

  useEffect(() => {
    if (user?.id) {
      setLoadingTable(true);
      // El backend valida `q` con @MinLength(2) @MaxLength(64): 1 carácter o más
      // de 64 devuelven 400. Se corta acá, sin request y conservando las filas
      // actuales.
      const q = (query || "").trim();
      if (q.length === 1 || q.length > 64) {
        showToast(t("La búsqueda debe tener entre 2 y 64 caracteres."), "warning");
        setLoading(false);
        setLoadingTable(false);
        return;
      }
      const newQuery = debounce(() => {
        // Mismo consumo que allOrders: endpoint paginado 1-based (pageSelected
        // es 0-based) con búsqueda unificada `q` y shape { docs, totalDocs }.
        // `silent`: el error se pinta en la vista (EmptyState), no como toast.
        getAllPackages({
          id: user?.id,
          limit: docsPerPage,
          page: pageSelected + 1,
          ...(q ? { q } : {}),
          orderField: filters?.orderBy || "_id",
          coms: filters?.coms,
          zipCode: filters?.zipCode,
          status: 4,
          startDate:
            filters?.date && moment(filters?.date).format("MM/DD/YYYY"),
          endDate:
            filters?.date &&
            moment(filters?.date).add(1, "days").format("MM/DD/YYYY"),
          silent: true,
        })
          .then((res) => {
            if (res?.success && res?.data) {
              setData(res.data.docs || []);
              setTotalDocs(res.data.totalDocs || 0);
              setLoadError(null);
            } else {
              setLoadError(res?.error || {});
            }
            setLoading(false);
            setLoadingTable(false);
          })
          .catch((err) => {
            setLoadError(err || {});
            setLoading(false);
            setLoadingTable(false);
          });
      }, 1000);
      newQuery();
    }
  }, [user, isLoading, docsPerPage, query, filters, pageSelected, reloadKey]);

  useEffect(() => {
    // Guard: sin user.id el endpoint resolvería a `status-packages/undefined`
    // (3 requests inútiles que dejan los KPIs en 0). Esperamos a tener id.
    if (!user?.id) return;
    // Shape real de getAllPackagesByStatus (status-packages/:id): la respuesta
    // expone el conteo en `total` (no `result`/`count` legacy). Se lee tal cual.
    getAllPackagesByStatus({
      id: user?.id,
      state: 2,
      startDate: showResume?.startDate,
      endDate: showResume?.endDate,
    }).then((data) => data?.data?.total && setInStorage(data?.data?.total));
    getAllPackagesByStatus({
      id: user?.id,
      state: 1,
      startDate: showResume?.startDate,
      endDate: showResume?.endDate,
    }).then((data) => data?.data?.total && setPicked(data?.data?.total));
    getAllPackagesByStatus({
      id: user?.id,
      state: 4,
      startDate: showResume?.startDate,
      endDate: showResume?.endDate,
    }).then((data) => {
      if (data?.data?.total) {
        setDelivered(data?.data?.total);
      }
    });
  }, [showResume, user]);

  useEffect(() => {
    const parsedData = data?.map((element) => {
      return {
        correlative: {
          value: (
            <div className="custom-center-inner-td ml-3">
              <span>{element?.followingNumber}</span>
            </div>
          ),
          type: "correlative",
          comparative: element?.followingNumber,
        },
        receiver: (
          <div className="d-grid">
            <span>{element?.receiver_name}</span>
            <span>{element?.receiver_phone}</span>
          </div>
        ),
        // Mismo shape que allOrders (varios <span> hermanos): PackageCard
        // (cards mobile) lee props.children como lista para armar la dirección.
        destiny: (
          <div className="d-grid custom-center-inner-td">
            <span className="text-truncate">
              {element?.shipping_address?.address_line}
            </span>
            <span className="custom-table-city-name">
              {element?.shipping_address?.city?.name}
            </span>
          </div>
        ),
        logistics: (
          <div className="custom-center-inner-td">
            <span>
              {element?.currentCarrier?.carrierType === "commerce_fleet"
                ? element?.senderInfo?.businessName || "N/A"
                : element?.deliveryInfo?.deliveryBusinessName || "N/A"}
            </span>
            {element?.currentCarrier?.carrierType === "commerce_fleet" && (
              <span className="badge-fleet-own">{t("Flota propia")}</span>
            )}
          </div>
        ),
        ingresed: (
          <div className="custom-center-inner-td">
            <span>{moment(element?.updatedAt).format("DD/MM/YY HH:mm")}</span>
          </div>
        ),
        status: {
          statusPill: (
            <div className="custom-center-inner-td">
              <div className={`${packageStatusStyles[element?.status]}`}>
                {packageStatus[element?.status]}
              </div>
            </div>
          ),
          value: (
            <div className="custom-center-inner-td">{element?.status}</div>
          ),
          status: element?.status,
        },
      };
    });
    setTableData(parsedData);
  }, [data]);

  // Back/forward — re-siembra la búsqueda desde ?q=. No-op en el primer render
  // (el estado ya se sembró igual), así no dispara un fetch extra.
  useEffect(() => {
    setQuery((prev) => (prev === qParam ? prev : qParam));
  }, [qParam]);

  // Back/forward — re-siembra el rango (KPIs) desde la URL: con ?preset=custom se
  // reconstruye desde ?from/?to; con cualquier otro preset se deriva de
  // rangeFromPreset. Guard por igualdad para no duplicar el primer render ni el
  // cambio propio de handleRangeChange.
  useEffect(() => {
    let startDate;
    let endDate;
    if (datePreset === "custom") {
      if (!fromParam || !toParam) return;
      startDate = moment(fromParam, "YYYY-MM-DD").format("MM/DD/YYYY");
      endDate = moment(toParam, "YYYY-MM-DD").format("MM/DD/YYYY");
    } else {
      const [s, e] = rangeFromPreset(datePreset);
      startDate = s.format("MM/DD/YYYY");
      endDate = e.format("MM/DD/YYYY");
    }
    setShowResume((prev) =>
      prev.startDate === startDate && prev.endDate === endDate
        ? prev
        : { startDate, endDate }
    );
  }, [datePreset, fromParam, toParam]);

  // KPIs por estado (prefiltro visual). El color del ícono/barra de cada card
  // usa el MISMO token de estado que su pill de fila (paridad pill↔card):
  // Recolectados/En bodega → info, Entregados → success.
  const totalCards = [
    { title: t("En bodega"), icon: Warehouse, tone: "info", value: inStorage },
    { title: t("Recolectados"), icon: Package, tone: "info", value: picked },
    { title: t("Entregados"), icon: CheckCircle2, tone: "success", value: delivered },
  ];

  const cardsTotal = inStorage + picked + delivered;

  const headers = [
    t("Correlativo"),
    t("Cliente"),
    t("Dirección de entrega"),
    t("Logística"),
    t("Última actualización"),
    t("Estado"),
    " ",
  ];

  const searchValue = (value) => {
    const newQuery = debounce(() => {
      setQuery(value);
      setQParam(value);
    }, 1000);
    newQuery();
  };

  // Cambio de rango desde el DateRangeFilter (preset rápido o personalizado).
  // Recibe fechas "YYYY-MM-DD"; actualiza el scope (showResume) que recalcula
  // los KPIs. No toca el fetch de la tabla (la tabla filtra por filters.date).
  const handleRangeChange = (startYMD, endYMD, preset) => {
    // preset + from + to en UNA sola navegación (sin pisarse). 'custom' ⇒
    // persistir el rango concreto en ?from/?to; cualquier otro preset ⇒
    // limpiarlos (se derivan del preset y la URL queda limpia, default "" los omite).
    setRange(
      preset === "custom"
        ? { preset, from: startYMD, to: endYMD }
        : { preset, from: "", to: "" }
    );
    setShowResume({
      startDate: moment(startYMD).format("MM/DD/YYYY"),
      endDate: moment(endYMD).format("MM/DD/YYYY"),
    });
  };

  if (loading) {
    // Skeleton shape-matched: SearchBar + header con título + 3 KPI cards + tabla.
    return (
      <div className="w-100 all-orders-container mt-6" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <Skeleton className="h-10 w-full rounded-lg" style={{ marginBottom: "1rem" }} />
        <div className="orders-header-container">
          <h2 className="orders-header-container__title">{t("Pedidos entregados")}</h2>
        </div>
        <SummaryCardsSkeleton count={totalCards.length} />
        <div className="all-orders-container__table">
          {/* +1 por la columna de checkbox que CustomTable antepone a los headers. */}
          <TableSkeleton rows={Math.min(docsPerPage || 8, 10)} columns={headers.length + 1} />
        </div>
      </div>
    );
  }

  if (loadError) {
    // Fallo de API ≠ lista vacía: error visible con reintento (vuelve al
    // skeleton mientras refetchea) en lugar de skeleton infinito.
    // El errorInfo del handler ya trae el texto resuelto (`message`); uno
    // inesperado (throw) se resuelve acá desde el body del backend.
    const loadErrorText = isNetworkError(loadError)
      ? t("Revisá tu conexión y volvé a intentarlo.")
      : loadError?.originalError
      ? loadError.message
      : apiErrorMessage(loadError, t("Error al cargar los paquetes. Por favor intenta nuevamente."));
    return (
      <div className="w-100 all-orders-container mt-6">
        <div className="orders-header-container">
          <h2 className="orders-header-container__title">{t("Pedidos entregados")}</h2>
        </div>
        <div className="rounded-lg border border-border bg-bg-muted" role="alert">
          <EmptyState
            icon={<AlertTriangle />}
            title={t("No pudimos cargar los pedidos entregados")}
            description={loadErrorText}
            action={
              <Button
                variant="outline"
                iconLeft={<RefreshCw size={16} aria-hidden />}
                onClick={() => {
                  setLoading(true);
                  setReloadKey((k) => k + 1);
                }}
              >
                {t("Reintentar")}
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-100 all-orders-container mt-6">
      <SearchBar onChange={searchValue} loading={loadingTable} value={q0} />
      {viewEvidence ? (
        <EvidenceView
          selectedPackage={packageEvidence}
          completePackages={data}
          setViewEvidence={setViewEvidence}
        />
      ) : (
        <>
          <div className="orders-header-container">
            <h2 className="orders-header-container__title">
              {t("Pedidos entregados")}
            </h2>
            <div className="summary-cards-header__date-range">
              <DateRangeFilter value={datePreset} onChange={handleRangeChange} />
            </div>
          </div>

          <div className="summary-cards-container">
            {totalCards.map((item, index) => {
              const Icon = item.icon;
              const pct =
                cardsTotal > 0 ? Math.round((item.value / cardsTotal) * 100) : 0;
              return (
                <div
                  key={index}
                  className={`summary-card summary-card--${item.tone}`}
                >
                  <div className="summary-card__top">
                    <span className="summary-card__icon" aria-hidden="true">
                      <Icon size={18} strokeWidth={2} />
                    </span>
                    <span className="summary-card__value">
                      {item.value.toLocaleString()}
                    </span>
                  </div>
                  <div className="summary-card__label">{item.title}</div>
                  <div className="summary-card__bar">
                    <div
                      className="summary-card__bar-fill"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <div className="all-orders-container__table">
            <CustomTable
              title={t("Todos los pedidos")}
              headers={headers}
              data={tableData}
              totalDocs={totalDocs}
              page={setPageSelected}
              actions
              completeData={data}
              docsPerPage={docsPerPage}
              loadingTable={loadingTable}
              pageSelected={pageSelected}
              setViewEvidence={setViewEvidence}
              setPackageEvidence={setPackageEvidence}
              filters={filters}
              setFilters={setFilters}
              statusDisabled={true}
            />
          </div>
        </>
      )}
    </div>
  );
}
